import React, { useState, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/forms/icon.component';

interface DataStatistic {
  label: string;
  total: number;
  type: string;
  icon: string;
}

interface Tab {
  key: string;
  label: string;
}

interface PrincipalCardProps {
  dataStatistics?: DataStatistic[];
  onClick: () => void;
  onRefresh?: () => void;
  onTabChange?: (key: string) => void;
  btnBack?: boolean;
  btnCreate?: boolean;
  btnRefresh?: boolean;
  returnExtrict?: string;
  title: string;
  titleBolder: string;
  subtitle: string;
  breadcrumbItems: string[];
  iconButton?: string;
  textButton?: string;
  tabs?: Tab[];
  customActionContent?: ReactNode;
  hideDefaultActions?: boolean;
}

const PrincipalCard: React.FC<PrincipalCardProps> = ({
  dataStatistics,
  onClick,
  onRefresh,
  onTabChange,
  btnBack = false,
  btnCreate = false,
  btnRefresh = false,
  title = '',
  returnExtrict = '',
  titleBolder = '',
  subtitle = '',
  breadcrumbItems = [],
  iconButton = '',
  textButton = '',
  tabs = [],
  customActionContent,
  hideDefaultActions = false
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(tabs.length > 0 ? tabs[0].key : '');

  return (
    <>
      <div className="d-flex justify-content-between align-items-center">
        <div className="d-flex flex-column">
          <ul className="breadcrumb fw-semibold fs-base my-1" aria-label="breadcrumb">
            {breadcrumbItems.map((item) => (
              <li key={item} className="breadcrumb-item">
                {t(item)}
              </li>
            ))}
          </ul>
          <div className="d-flex flex-column mb-0">
            <h1 className="fw-bold fs-1">
              {title}
              <span className="text-info fw-bolder ms-2">{titleBolder}</span>
            </h1>
            <p className="fs-6 text">{subtitle}</p>
          </div>
        </div>
        {btnBack && (
          <button
            className="d-flex align-items-center text-nowrap py-1 btn btn-link"
            onClick={() => {
              if (returnExtrict && returnExtrict.trim() !== '') {
                navigate(returnExtrict);
              } else {
                navigate(-1);
              }
            }}
            aria-label={t('general.return')}
          >
            <span className="symbol symbol-circle symbol-50px hover-scale">
              <span className="symbol-label bg-light-info">
                <Icon icon="ki-double-left" size="fs-2x" type="info" />
              </span>
            </span>
            <span className="ms-2 fw-bolder">{t('general.return')}</span>
          </button>
        )}
      </div>
      <div className="mb-7 bg-default d-flex justify-content-start">
        <div className="card">
          <div className="badge badge-lg badge-white">
            {tabs.map(({ key, label }) => (
              <React.Fragment key={key}>
                <div className="width-fit-content d-inline-block cursor-pointer">
                  <button
                    className={`btn rounded-12 align-items-center p-3 hover-scale ${
                      activeTab === key ? 'bg-light text-dark' : ''
                    }`}
                    type="button"
                    onClick={() => {
                      setActiveTab(key);
                      onTabChange?.(key);
                    }}
                  >
                    {activeTab === key && (
                      <Icon
                        icon="ki-check-circle"
                        size="fs-1"
                        className="fw-bolder"
                        type="primary"
                      />
                    )}
                    <span className="fw-bolder ms-1">{label}</span>
                  </button>
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
      {dataStatistics && (
        <div className="card mb-5 mb-xl-10 animate-fadeInUp animate-delay-1">
          <div className="card-body pt-9 pb-0">
            <div className="d-flex flex-wrap flex-sm-nowrap mb-3">
              <div className="me-7 mb-4">
                <Icon
                  icon="ki-chart"
                  size="fs-4x"
                  type="primary"
                  className="hover-scale me-2 mt-10"
                />
              </div>
              <div className="flex-grow-1">
                <div className="d-flex justify-content-between align-items-start flex-wrap mb-4">
                  <div className="d-flex flex-column">
                    <div className="d-flex align-items-center mb-2">
                      <span className="text-gray-900 fs-2 fw-bold me-1">
                        {t('general.estatistics')}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="d-flex flex-wrap flex-stack">
                  <div className="d-flex flex-column flex-grow-1 pe-8">
                    <div className="d-flex flex-wrap">
                      {dataStatistics?.map(({ label, total, type, icon }) => (
                        <div
                          key={label}
                          className="border border-gray-300 border-dashed rounded min-w-125px py-3 px-4 me-6 mb-3"
                        >
                          <div className="d-flex align-items-center">
                            <Icon
                              icon={icon}
                              size="fs-2x"
                              type={type}
                              className="hover-scale me-2"
                            />
                            <span className="fs-2 fw-bold">
                              {' '}
                              {typeof total === 'number'
                                ? total.toLocaleString()
                                : Number(total).toLocaleString()}
                            </span>
                          </div>
                          <span className="fw-semibold fs-4">{label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="d-flex align-items-center flex-nowrap text-nowrap py-1">
                    {customActionContent && <div className="me-4">{customActionContent}</div>}
                    {!hideDefaultActions && (
                      <>
                        {btnRefresh && (
                          <button
                            className="btn btn-light-info hover-scale fw-bolder me-4"
                            onClick={onRefresh}
                          >
                            <Icon icon="ki-abstract-20" size="fs-1" />
                            {t('general.update')}
                          </button>
                        )}
                        {btnCreate && (
                          <button className="btn btn-dark hover-scale fw-bolder" onClick={onClick}>
                            <Icon icon={iconButton} size="fs-2" />
                            {textButton}
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PrincipalCard;
